import React, { useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth.jsx";
import SiteFooter from "./SiteFooter.jsx";
import HPCSAAttestationModal from "./HPCSAAttestationModal.jsx";
import TrialBanner from "./TrialBanner.jsx";
import {
  LayoutDashboard, BookOpen, Pill, ShieldAlert, Calculator,
  Sparkles, Star, Search, Settings as SettingsIcon, LogOut,
  Menu, X, Activity, ShieldCheck, Baby, Syringe, Zap, FileText
} from "lucide-react";

const navItems = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/protocols", label: "Protocols", icon: BookOpen },
  { to: "/app/drugs", label: "Drugs", icon: Pill },
  { to: "/app/paediatric-drugs", label: "Paediatric Doses", icon: Baby },
  { to: "/app/interactions", label: "Interactions", icon: ShieldAlert },
  { to: "/app/antibiotic-guide", label: "Antibiotic Guide", icon: Syringe },
  { to: "/app/calculators", label: "Calculators", icon: Calculator },
  { to: "/app/quick-reference", label: "Quick Reference", icon: Zap },
  { to: "/app/icd10", label: "ICD-10 Codes", icon: FileText },
  { to: "/app/favorites", label: "Favorites", icon: Star },
  { to: "/app/search", label: "Search", icon: Search },
];

export default function AppLayout() {
  const { user, logout, refresh } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [attested, setAttested] = useState(false);

  const doLogout = () => {
    logout();
    nav("/");
  };

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 flex">
      {/* Sidebar */}
      <aside
        className={`fixed md:static inset-y-0 left-0 z-40 w-64 bg-[#F3F1EA] border-r border-border flex-col transform transition-transform duration-200 ${
          open ? "translate-x-0" : "-translate-x-full md:translate-x-0"
        } md:flex`}
      >
        <div className="h-16 flex items-center gap-2 px-6 border-b border-border">
          <Link
            to="/app/dashboard"
            onClick={() => setOpen(false)}
            data-testid="sidebar-logo-link"
            className="flex items-center gap-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand rounded-md"
          >
            <div className="w-9 h-9 rounded-xl bg-[#0B5FFF] text-white flex items-center justify-center shadow-soft">
              <Activity className="w-5 h-5" strokeWidth={1.75} />
            </div>
            <div>
              <div className="font-heading font-bold text-[#111827] leading-none text-lg">RapidCare</div>
              <div className="text-[10px] tracking-widest text-slate-500 uppercase">SA Clinical</div>
            </div>
          </Link>
        </div>
        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1">
          {navItems.map(({ to, label, icon: Icon }) => (
            <NavLink
              key={to}
              to={to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive
                    ? "bg-white text-[#111827] shadow-soft"
                    : "text-slate-600 hover:bg-black/[0.04] hover:text-[#111827]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && <span className="absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-full bg-brand" />}
                  <Icon className={`w-4 h-4 ${isActive ? "text-brand" : ""}`} strokeWidth={1.5} />
                  {label}
                </>
              )}
            </NavLink>
          ))}
          {user?.role === "admin" && (
            <NavLink
              to="/app/admin"
              onClick={() => setOpen(false)}
              data-testid="nav-admin"
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                  isActive ? "bg-white text-amber-900 shadow-soft" : "text-amber-700 hover:bg-amber-500/10"
                }`
              }
            >
              <ShieldCheck className="w-4 h-4" strokeWidth={1.5} />
              Admin
            </NavLink>
          )}
        </nav>
        <div className="border-t border-border p-3">
          <NavLink
            to="/app/settings"
            onClick={() => setOpen(false)}
            data-testid="nav-settings"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 ${
                isActive ? "bg-white text-[#111827] shadow-soft" : "text-slate-600 hover:bg-black/[0.04]"
              }`
            }
          >
            <SettingsIcon className="w-4 h-4" strokeWidth={1.5} /> Settings
          </NavLink>
          <button
            onClick={doLogout}
            data-testid="logout-btn"
            className="w-full mt-1 flex items-center gap-3 px-3 py-2 rounded-xl text-sm font-medium text-slate-600 hover:bg-black/[0.04] transition-all duration-200"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} /> Sign out
          </button>
        </div>
      </aside>

      {/* Backdrop (mobile) */}
      {open && (
        <button
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-slate-900/40 z-30 md:hidden"
          aria-label="Close menu"
        />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 md:ml-0">
        <header className="h-16 glass border-b border-border flex items-center justify-between px-4 md:px-8 sticky top-0 z-20">
          <button
            onClick={() => setOpen((v) => !v)}
            className="md:hidden p-2 text-slate-600"
            data-testid="menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-sm font-semibold text-[#111827]">{user?.full_name}</div>
              <div className="text-xs text-slate-500 capitalize">
                {user?.role} • {user?.subscription_status}
              </div>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#0B5FFF] text-white flex items-center justify-center font-semibold ring-2 ring-brand/30">
              {user?.full_name?.charAt(0).toUpperCase() || "U"}
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 overflow-x-hidden bg-[#F8FAFC]">
          <TrialBanner />
          <Outlet />
        </main>
        <SiteFooter />
      </div>

      {/* Quarterly HPCSA re-attestation (Option D) — non-dismissable until confirmed */}
      {user?.hpcsa_attestation_due && !attested && user.role !== "admin" && (
        <HPCSAAttestationModal
          user={user}
          onConfirmed={async () => { setAttested(true); await refresh?.(); }}
        />
      )}
    </div>
  );
}
